// Client-side track export: GPX 1.1 serialization and file download. Tracks
// whose row still carries the original `sourceGpx` upload are offered that
// file verbatim instead of a regenerated one (see TrackExportButtons).
import { flatCoords, type Geometry } from "./geo";

/** "Sněžka sunrise loop" → "snezka-sunrise-loop" (fallback "track"). */
export function slugify(name: string): string {
  const slug = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "track";
}

function escapeXml(s: string): string {
  return s.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

/**
 * Serialize a (Multi)LineStringZ GeoJSON geometry to GPX 1.1 — one <trk>, one
 * <trkseg> per line part, elevation from the Z coordinate. When `pointTimes`
 * aligns 1:1 with the geometry points, each point also gets a <time> element.
 */
export function trackToGpx(geom: Geometry, name: string, pointTimes?: string[] | null): string {
  const segments =
    geom.type === "MultiLineString"
      ? (geom.coordinates as number[][][])
      : geom.type === "LineString"
        ? [geom.coordinates as number[][]]
        : [];
  const times = pointTimes && pointTimes.length === (flatCoords(geom)?.length ?? -1) ? pointTimes : null;

  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="gaea" xmlns="http://www.topografix.com/GPX/1/1">',
    "  <trk>",
    `    <name>${escapeXml(name)}</name>`,
  ];
  let idx = 0;
  for (const seg of segments) {
    lines.push("    <trkseg>");
    for (const c of seg) {
      const [lon, lat, ele] = c;
      const parts = [`      <trkpt lat="${lat}" lon="${lon}">`];
      if (typeof ele === "number") parts.push(`<ele>${ele}</ele>`);
      if (times) parts.push(`<time>${escapeXml(times[idx]!)}</time>`);
      parts.push("</trkpt>");
      lines.push(parts.join(""));
      idx++;
    }
    lines.push("    </trkseg>");
  }
  lines.push("  </trk>", "</gpx>", "");
  return lines.join("\n");
}

/**
 * Export-grade GeoJSON: drops pdbq's legacy `crs` member (invalid per
 * RFC 7946) but keeps Z coordinates — unlike cleanGeometry, which strips
 * elevation for MapLibre.
 */
export function geometryToGeoJson(geom: Geometry): string {
  const { crs: _crs, ...rest } = geom;
  return JSON.stringify(rest);
}

/** Trigger a client-side file download of `text`. */
export function download(filename: string, mime: string, text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
